import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import './DisponibilidadPage.css';

// Disponibilidad de equipos para una fecha: por nombre, cuántas unidades
// operativas hay, cuántas en depósito, cuántas comprometidas en eventos que
// pisan ese día y cuántas quedan libres. Usa GET /inventory/disponibilidad
// (la misma lógica de ventanas que usan cotizaciones y checklists).

const norm = s => (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

function todayStr() {
  const t = new Date();
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
}

function stateOf(r) {
  if (r.free === 0 && r.committed > 0) return 'agotado';
  if (r.committed > 0) return 'parcial';
  return 'libre';
}

export default function DisponibilidadPage() {
  const [date, setDate] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [soloComprometidos, setSoloComprometidos] = useState(false);
  const [open, setOpen] = useState(() => new Set());

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    api.get('/inventory/disponibilidad', { params: { date } })
      .then(r => { if (alive) setData(r.data); })
      .catch(e => { if (alive) setError(e.response?.data?.error || 'No se pudo cargar la disponibilidad.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [date]);

  const rows = useMemo(() => {
    if (!data?.by_name) return [];
    const q = norm(search.trim());
    return Object.entries(data.by_name)
      .map(([name, r]) => ({ name, ...r }))
      .filter(r => r.operational > 0)
      .filter(r => !q || q.split(/\s+/).every(w => norm(r.name).includes(w)))
      .filter(r => !soloComprometidos || r.committed > 0)
      .sort((a, b) => (b.committed - a.committed) || a.name.localeCompare(b.name, 'es'));
  }, [data, search, soloComprometidos]);

  const resumen = useMemo(() => {
    const all = Object.values(data?.by_name || {}).filter(r => r.operational > 0);
    return {
      total: all.length,
      comprometidos: all.filter(r => r.committed > 0).length,
      agotados: all.filter(r => r.committed > 0 && r.free === 0).length,
    };
  }, [data]);

  function toggle(name) {
    setOpen(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Disponibilidad de equipos</h1>
          <p className="page-subtitle">
            {resumen.total} equipos · {resumen.comprometidos} con compromiso ese día{resumen.agotados > 0 ? ` · ${resumen.agotados} sin libres` : ''}
          </p>
        </div>
        <Link to="/inventory" className="btn btn-ghost btn-sm">← Inventario</Link>
      </div>

      <div className="disp-toolbar">
        <input
          className="input disp-date"
          type="date"
          value={date}
          onChange={e => e.target.value && setDate(e.target.value)}
        />
        <button className="btn btn-ghost btn-sm" onClick={() => setDate(todayStr())}>Hoy</button>
        <input
          className="input disp-search"
          placeholder="Buscar equipo…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <button
          className={`btn btn-ghost btn-sm${soloComprometidos ? ' active-tab' : ''}`}
          onClick={() => setSoloComprometidos(v => !v)}
        >
          Solo comprometidos
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>
      ) : error ? (
        <div className="disp-empty">{error}</div>
      ) : rows.length === 0 ? (
        <div className="disp-empty">
          {search || soloComprometidos ? 'Sin resultados con esos filtros.' : 'No hay equipos operativos cargados.'}
        </div>
      ) : (
        <div className="disp-table">
          <div className="disp-head">
            <span>Equipo</span>
            <span className="disp-num">Operativas</span>
            <span className="disp-num">En depósito</span>
            <span className="disp-num">Comprometidas</span>
            <span className="disp-num">Libres</span>
            <span />
          </div>
          {rows.map(r => {
            const st = stateOf(r);
            const expandable = r.events.length > 0;
            const isOpen = open.has(r.name);
            return (
              <React.Fragment key={r.name}>
                <div
                  className={`disp-row disp-row--${st}${expandable ? ' disp-row--click' : ''}`}
                  onClick={expandable ? () => toggle(r.name) : undefined}
                >
                  <span className="disp-name">{r.name}</span>
                  <span className="disp-num">{r.operational}</span>
                  <span className="disp-num">{r.at_home}</span>
                  <span className="disp-num">{r.committed > 0 ? r.committed : '—'}</span>
                  <span className={`disp-num disp-free disp-free--${st}`}>{r.free}</span>
                  <span className="disp-caret">{expandable ? (isOpen ? '▾' : '▸') : ''}</span>
                </div>
                {expandable && isOpen && (
                  <div className="disp-events">
                    {r.events.map(ev => (
                      <div key={ev.id} className="disp-event">
                        <span className="disp-event-units">{ev.units} un.</span>
                        <span className="disp-event-name">{ev.name}</span>
                      </div>
                    ))}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
